import { useEffect, useMemo, useState } from 'react';
import { forEach, includes } from 'lodash';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { OnboardingTaskStatusEnum } from '@nxsys/constants/AppEnums';
import {
  OnboardingData,
  OnboardingTasks,
  UpdateOnboardingData,
} from '@nxsys/store/slices/prospect/types';
import getOnboardingStepAndNationalities from './getOnboardingStepAndNationalities';

export type Step = {
  label: string;
  key: string;
  completed: boolean;
  index: number;
};

export type OnboardingPayload =
  | OnboardingData
  | UpdateOnboardingData
  | null
  | undefined;

/**
 * Builds the visible step list for an onboarding payload.
 *
 * Shared by the admin flow (ViewOnboardingModal, acting on a prospect) and the
 * employee flow (EmployeeOnboarding, acting on the signed-in user) so the step
 * set, ordering and completion rules stay in one place.
 *
 * The step set depends on nationality: UK nationals verify their ID with a
 * passport (addID), everyone else proves right-to-work with a share code
 * (addShareCode). Steps the API marks 'N/A' are hidden entirely.
 */
const useOnboardingSteps = (data: OnboardingPayload) => {
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;

  const { nationalities, stepsTemp, stepsTemp1, stepsTemp2 } = useMemo(
    () => getOnboardingStepAndNationalities({ isBureau, isIreland }),
    [isBureau, isIreland],
  );

  const onboarding = useMemo(() => data?.onboarding, [data]);
  const onboardingTasks = useMemo(
    () => data?.prospectOnboarding?.onboardingTasks,
    [data],
  );

  const [nationality, setNationality] = useState<string | null>(null);

  // Re-seed from the payload whenever it changes (including a modal reopen).
  useEffect(() => {
    setNationality(onboarding?.nationality ?? null);
  }, [onboarding]);

  const steps = useMemo(() => {
    let tasks: Record<string, Step> = {};
    let index = 0;
    let tempSteps: Record<string, { label: string; key: string }> = stepsTemp;
    if (nationality) {
      if (!includes(nationalities, nationality)) {
        tempSteps = stepsTemp1;
      } else tempSteps = stepsTemp2;
    }
    forEach(tempSteps, (step, key) => {
      if (
        onboardingTasks &&
        onboardingTasks[key as keyof OnboardingTasks] !==
          OnboardingTaskStatusEnum.NA
      ) {
        tasks = {
          ...tasks,
          [key]: {
            ...step,
            index,
            completed: !!(
              onboardingTasks[key as keyof OnboardingTasks] ===
              OnboardingTaskStatusEnum.YES
            ),
          },
        };
        index++;
      }
    });

    return tasks;
  }, [onboardingTasks, nationality]);

  const completedSteps = useMemo(
    () => Object.values(steps).filter((step: Step) => step.completed).length,
    [steps],
  );
  const totalSteps = useMemo(() => Object.values(steps).length, [steps]);

  return {
    steps,
    completedSteps,
    totalSteps,
    nationality,
    setNationality,
  };
};

export default useOnboardingSteps;
